/**
 * Rewards page — always shows the management view with all tabs accessible.
 *
 * No wizard gate — if the program isn't configured, the management view
 * starts on the "ตั้งค่าโปรแกรม" tab. All other tabs are freely accessible.
 *
 * Plan 177, Epic 02 (original wizard) → Plan 1833 (free-form settings)
 * Plan 1933 — multi-store: a StoreSelector scopes the whole rewards admin to one
 * store's loyalty program (each store has separate points/rewards/campaigns).
 */

import { PageContainer } from "@/components/layout/PageContainer";
import { Loader2, Info } from "lucide-react";
import { useRewardsConfig } from "./api/rewardsAdminHooks";
import { ManagementView } from "./components/ManagementView";
import { StoreSelector } from "@/components/storefront/StoreSelector";
import { useStoreList } from "@/hooks/useStoreList";
import { useAuthStore } from "@/lib/store/authStore";

export function RewardsPage() {
  const configQuery = useRewardsConfig();
  const { data: stores = [] } = useStoreList();
  const activeStorefrontKey = useAuthStore((s) => s.activeStorefrontKey);

  const isMultiStore = stores.length > 1;
  const showAllStoresHint = isMultiStore && !activeStorefrontKey;

  return (
    <PageContainer noPadding>
      {configQuery.isLoading || !configQuery.data ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="min-h-screen py-6">
          {isMultiStore && (
            <div className="px-6 pb-4 space-y-2">
              <div className="flex items-center gap-3">
                <span className="text-sm text-muted-foreground">Store</span>
                <StoreSelector />
              </div>
              {showAllStoresHint && (
                <div className="flex items-start gap-2 rounded-lg bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
                  <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>
                    Each store has its own separate loyalty program — points, rewards, and
                    campaigns don't cross over. Select a store above to view or create its rewards.
                  </span>
                </div>
              )}
            </div>
          )}
          <ManagementView config={configQuery.data} />
        </div>
      )}
    </PageContainer>
  );
}
